import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { fMonthDayYearShort } from '../../../../utils/formatTime';
import { FIELDS, RECURRENCE_TYPE_VALUES, RECURRENCE_TYPES, WEEKDAYS } from '../constants';
import { formatLocalDate, getPluralLabel, parseDateOnlyAsLocal } from './utils';

RecurrenceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  formState: PropTypes.object.isRequired,
  setFields: PropTypes.func.isRequired,
};

// Helper to get ordinal (1st, 2nd, 3rd, 4th, etc.)
function getOrdinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// Helper to get weekday name from a date
function getWeekdayName(date) {
  return date.toLocaleDateString(undefined, { weekday: 'long' });
}

// Helper to get the ordinal and weekday for a given date
function getOrdinalAndWeekday(date) {
  const day = date.getDate();
  const weekday = getWeekdayName(date);
  const weekdayNum = date.getDay();
  // Find which occurrence of this weekday in the month
  let count = 0;
  for (let d = 1; d <= day; d++) {
    const temp = new Date(date.getFullYear(), date.getMonth(), d);
    if (temp.getDay() === date.getDay()) count++;
  }
  const ordinal = count === 5 ? 'last' : getOrdinal(count);
  return { ordinal, weekday, weekdayNum, weekOfMonth: count };
}

export default function RecurrenceDialog({ open, onCancel, onSave, formState, setFields }) {
  const today = useMemo(() => new Date(), []);
  const [validationErrors, setValidationErrors] = useState({
    endDate: false,
    endDateMessage: '',
    daysOfWeek: false,
  });

  // Helper to get local date (year, month, day) from a date string
  const getLocalDate = (dateString) => {
    if (!dateString) return today;

    const localDateOnly = parseDateOnlyAsLocal(dateString);
    if (localDateOnly) return localDateOnly;

    const d = new Date(dateString);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  // Determine the min date for recurrence end date (always local date part)
  const minRecurrenceEndDate = useMemo(() => {
    // Prefer ParentAppointmentDate if present
    if (formState.ParentAppointmentDate) {
      return getLocalDate(formState.ParentAppointmentDate);
    }
    // Else use AppointmentDateTime if present
    if (formState.AppointmentDateTime) {
      return getLocalDate(formState.AppointmentDateTime);
    }
    // Fallback to today
    return today;
  }, [formState.ParentAppointmentDate, formState.AppointmentDateTime, today]);

  // Max recurrence end date is one year from the selected start date anchor
  const maxRecurrenceEndDate = useMemo(() => {
    const maxDate = new Date(minRecurrenceEndDate);
    maxDate.setFullYear(maxDate.getFullYear() + 1);
    return maxDate;
  }, [minRecurrenceEndDate]);

  // Helper to get initial localFields state
  const getInitialLocalFields = () => ({
    RecurrenceType: formState.RecurrenceType || RECURRENCE_TYPE_VALUES.DAILY,
    RecurrenceFrequency: formState.RecurrenceFrequency || 1,
    RecurrenceEndDate: formState.RecurrenceEndDate || '',
    RecurrenceWeekOfMonth: formState.RecurrenceWeekOfMonth || 1,
    RecurrenceDaysOfWeek: formState.RecurrenceDaysOfWeek || [],
    RecurrenceDayOfMonth:
      formState.RecurrenceDayOfMonth ||
      (formState.AppointmentDateTime ? new Date(formState.AppointmentDateTime).getDate() : today.getDate()),
    IsRecurring: formState.IsRecurring || false,
  });

  // Local state for all recurrence fields
  const [localFields, setLocalFields] = useState(getInitialLocalFields);

  // Reset local state when dialog opens
  useEffect(() => {
    if (open) {
      setLocalFields(getInitialLocalFields());
      setValidationErrors({ endDate: false, endDateMessage: '', daysOfWeek: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Compute label for 'Monthly on the X Weekday' option
  const referenceDate = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), localFields.RecurrenceDayOfMonth),
    [today, localFields.RecurrenceDayOfMonth]
  );
  const { ordinal, weekday, weekdayNum, weekOfMonth } = useMemo(
    () => getOrdinalAndWeekday(referenceDate),
    [referenceDate]
  );
  const monthlyOnWeekdayLabel = useMemo(() => `Monthly on the ${ordinal} ${weekday}`, [ordinal, weekday]);

  // Handle local field changes
  const handleFieldChange = (field, value) => {
    setLocalFields((prev) => ({ ...prev, [field]: value }));

    if (field === FIELDS.RECURRENCE_END_DATE) {
      setValidationErrors((prev) => ({ ...prev, endDate: false, endDateMessage: '' }));
    }

    if (field === FIELDS.RECURRENCE_DAYS_OF_WEEK) {
      setValidationErrors((prev) => ({ ...prev, daysOfWeek: false }));
    }
  };

  // Keep RecurrenceWeekOfMonth in sync when RecurrenceType is MONTHLY_BY_WEEKDAY
  useEffect(() => {
    if (localFields.RecurrenceType === RECURRENCE_TYPE_VALUES.MONTHLY_BY_WEEKDAY) {
      handleFieldChange(FIELDS.RECURRENCE_WEEK_OF_MONTH, weekOfMonth);
      handleFieldChange(FIELDS.RECURRENCE_DAYS_OF_WEEK, [weekdayNum]);
    }
  }, [localFields.RecurrenceType, weekOfMonth, weekdayNum]);

  // Helper to check if current type is any monthly variant
  const isMonthlyType = (type) => {
    return type === RECURRENCE_TYPE_VALUES.MONTHLY_BY_DATE || type === RECURRENCE_TYPE_VALUES.MONTHLY_BY_WEEKDAY;
  };

  // Save: push local state to parent
  const handleSave = () => {
    const hasEndDate = Boolean(localFields.RecurrenceEndDate);
    const selectedEndDate = hasEndDate ? getLocalDate(localFields.RecurrenceEndDate) : null;
    const isEndDateInRange =
      hasEndDate && selectedEndDate >= minRecurrenceEndDate && selectedEndDate <= maxRecurrenceEndDate;
    const hasWeeklyDays =
      localFields.RecurrenceType !== RECURRENCE_TYPE_VALUES.WEEKLY ||
      (Array.isArray(localFields.RecurrenceDaysOfWeek) && localFields.RecurrenceDaysOfWeek.length > 0);

    if (!hasEndDate || !isEndDateInRange || !hasWeeklyDays) {
      setValidationErrors({
        endDate: !hasEndDate || !isEndDateInRange,
        endDateMessage: !hasEndDate
          ? 'End date is required.'
          : `End date must be between ${fMonthDayYearShort(minRecurrenceEndDate)} and ${fMonthDayYearShort(
              maxRecurrenceEndDate
            )}.`,
        daysOfWeek: !hasWeeklyDays,
      });
      return;
    }

    // Build the fields object to update in bulk
    const fields = {
      [FIELDS.RECURRENCE_TYPE]: localFields.RecurrenceType,
      [FIELDS.RECURRENCE_FREQUENCY]: Number(localFields.RecurrenceFrequency),
      [FIELDS.RECURRENCE_END_DATE]: localFields.RecurrenceEndDate,
      [FIELDS.IS_RECURRING]: true,
    };

    if (localFields.RecurrenceType === RECURRENCE_TYPE_VALUES.WEEKLY) {
      fields[FIELDS.RECURRENCE_DAYS_OF_WEEK] = Array.isArray(localFields.RecurrenceDaysOfWeek)
        ? [...localFields.RecurrenceDaysOfWeek].sort()
        : localFields.RecurrenceDaysOfWeek;
      fields[FIELDS.RECURRENCE_DAY_OF_MONTH] = null;
      fields[FIELDS.RECURRENCE_WEEK_OF_MONTH] = null;
    } else if (localFields.RecurrenceType === RECURRENCE_TYPE_VALUES.MONTHLY_BY_DATE) {
      fields[FIELDS.RECURRENCE_DAY_OF_MONTH] = localFields.RecurrenceDayOfMonth;
      fields[FIELDS.RECURRENCE_DAYS_OF_WEEK] = null;
      fields[FIELDS.RECURRENCE_WEEK_OF_MONTH] = null;
    } else if (localFields.RecurrenceType === RECURRENCE_TYPE_VALUES.MONTHLY_BY_WEEKDAY) {
      fields[FIELDS.RECURRENCE_WEEK_OF_MONTH] = localFields.RecurrenceWeekOfMonth;
      fields[FIELDS.RECURRENCE_DAYS_OF_WEEK] = Array.isArray(localFields.RecurrenceDaysOfWeek)
        ? [...localFields.RecurrenceDaysOfWeek].sort()
        : localFields.RecurrenceDaysOfWeek;
      fields[FIELDS.RECURRENCE_DAY_OF_MONTH] = null;
    } else {
      // For daily or other types, null all
      fields[FIELDS.RECURRENCE_WEEK_OF_MONTH] = null;
      fields[FIELDS.RECURRENCE_DAYS_OF_WEEK] = null;
      fields[FIELDS.RECURRENCE_DAY_OF_MONTH] = null;
    }

    setFields(fields);
    onSave();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onCancel();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Custom recurrence</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography>Repeat every</Typography>
            <TextField
              type="number"
              size="small"
              value={localFields.RecurrenceFrequency}
              onChange={(e) => {
                const val = e.target.value;
                // allow backspace / empty field
                if (val === '') {
                  handleFieldChange(FIELDS.RECURRENCE_FREQUENCY, '');
                  return;
                }

                const num = Number(val);

                // only allow valid positive integers
                if (Number.isInteger(num) && num > 0) {
                  handleFieldChange(FIELDS.RECURRENCE_FREQUENCY, num);
                }
              }}
              onBlur={() => {
                let num = parseInt(localFields.RecurrenceFrequency, 10);
                if (Number.isNaN(num) || num < 1) num = 1;
                handleFieldChange(FIELDS.RECURRENCE_FREQUENCY, num);
              }}
              inputProps={{ min: 1, style: { width: 60 } }}
            />
            <FormControl size="small">
              <Select
                value={
                  isMonthlyType(localFields.RecurrenceType)
                    ? RECURRENCE_TYPE_VALUES.MONTHLY_BY_DATE
                    : localFields.RecurrenceType || RECURRENCE_TYPE_VALUES.DAILY
                }
                onChange={(e) => handleFieldChange(FIELDS.RECURRENCE_TYPE, e.target.value)}
              >
                {RECURRENCE_TYPES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {getPluralLabel(opt.label, parseInt(localFields.RecurrenceFrequency, 10) || 1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {localFields.RecurrenceType === RECURRENCE_TYPE_VALUES.WEEKLY && (
            <Box>
              <Typography sx={{ mb: 1 }}>Repeat on</Typography>
              <ToggleButtonGroup
                value={localFields.RecurrenceDaysOfWeek || []}
                onChange={(_, newDays) => {
                  if (newDays.length > 0) handleFieldChange(FIELDS.RECURRENCE_DAYS_OF_WEEK, newDays);
                }}
                aria-label="repeat on weekdays"
                size="small"
                exclusive={false}
                sx={(theme) => ({
                  '& .MuiToggleButtonGroup-middleButton, & .MuiToggleButtonGroup-lastButton': {
                    borderLeft: `2px solid ${theme.palette.divider}`,
                  },
                })}
              >
                {WEEKDAYS.map((d) => (
                  <ToggleButton
                    key={d.value}
                    value={d.value}
                    aria-label={d.label}
                    sx={(theme) => ({
                      borderRadius: '50% !important',
                      width: 40,
                      height: 40,
                      mx: 0.5,
                      p: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: 16,
                      border: `2px solid ${theme.palette.divider}`,
                      backgroundColor: 'background.paper',
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        borderColor: theme.palette.primary.main,
                      },
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    })}
                  >
                    {d.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {validationErrors.daysOfWeek && (
                <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                  Select at least one day of week.
                </Typography>
              )}
            </Box>
          )}
          {isMonthlyType(localFields.RecurrenceType) && (
            <FormControl
              size="small"
              sx={{ minWidth: 240, maxWidth: 320, border: '0.5px solid', borderRadius: 1, p: 0.5 }}
            >
              <Select
                value={localFields.RecurrenceType}
                onChange={(e) => {
                  // Set RecurrenceType to the selected value
                  handleFieldChange(FIELDS.RECURRENCE_TYPE, e.target.value);
                  // If selecting MONTHLY_BY_DATE, ensure RecurrenceDayOfMonth is set (if needed)
                  if (e.target.value === RECURRENCE_TYPE_VALUES.MONTHLY_BY_DATE) {
                    handleFieldChange(FIELDS.RECURRENCE_DAY_OF_MONTH, localFields.RecurrenceDayOfMonth);
                  }
                }}
                displayEmpty
              >
                <MenuItem key="onDay" value={RECURRENCE_TYPE_VALUES.MONTHLY_BY_DATE}>
                  {`Monthly on day ${localFields.RecurrenceDayOfMonth}`}
                </MenuItem>
                <MenuItem key="onWeekday" value={RECURRENCE_TYPE_VALUES.MONTHLY_BY_WEEKDAY}>
                  {monthlyOnWeekdayLabel}
                </MenuItem>
              </Select>
            </FormControl>
          )}
          <Box>
            <Typography sx={{ minWidth: 28 }}>Ends on</Typography>
            <TextField
              type="date"
              size="small"
              value={formatLocalDate(getLocalDate(localFields.RecurrenceEndDate))}
              onChange={(e) => handleFieldChange(FIELDS.RECURRENCE_END_DATE, e.target.value)}
              sx={{ minWidth: 150 }}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: formatLocalDate(minRecurrenceEndDate),
                max: formatLocalDate(maxRecurrenceEndDate),
              }}
              error={validationErrors.endDate}
              helperText={validationErrors.endDate ? validationErrors.endDateMessage : ''}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
